import { type ReactNode } from 'react'
import {
  InfoSectionItem,
  InfoSectionItems,
} from '@/features/admin/components/InfoSectionItems'
import { formatMoney } from '@/utils/money-util'
import {
  formatDate,
  formatSum,
} from '@/features/admin/scope-change-requests/scope-change-request-formatters'
import { match } from '@/utils/pattern-match-util'
import styles from './InfoSection.module.css'
import { type ScopeChangeRequestStatusTone } from '@/features/admin/scope-change-requests/scope-change-request-formatters'

export interface InfoSectionPriceBreakDown {
  previousTotalPrice?: number | null
  totalPriceDiff: number
  newTotalPrice?: number | null
  previousTotalProPrice?: number | null
  totalProPriceDiff: number
  newTotalProPrice?: number | null
}

export interface InfoSectionProps {
  jobDisplayName: string
  jobShortId: string
  legendName: string
  workspaceName: string
  createdByName: string
  createTime: Date
  open: string
  /**
   * `null` when the job has no bundle.
   */
  bundleInfo: ReactNode | null
  isRejected: boolean
  priceBreakDown: InfoSectionPriceBreakDown
  /**
   * `null` when no percentage change was applied.
   */
  percentageNote: ReactNode | null
  statusTone: ScopeChangeRequestStatusTone
  statusText: string
  sidePanelStatusText: string
}

export function InfoSection({
  jobDisplayName,
  jobShortId,
  legendName,
  workspaceName,
  createdByName,
  createTime,
  open,
  bundleInfo,
  isRejected,
  priceBreakDown,
  percentageNote,
  statusTone,
  statusText,
  sidePanelStatusText,
}: InfoSectionProps) {
  return (
    <div className={styles.root}>
      <h2 className={styles.infoTitle}>{jobDisplayName}</h2>
      <InfoSectionItems>
        <InfoSectionItem label="Job ID">{jobShortId}</InfoSectionItem>
        <InfoSectionItem label="Legend">{legendName}</InfoSectionItem>
        <InfoSectionItem label="Workspace">{workspaceName}</InfoSectionItem>
        <InfoSectionItem label="Requested by">{createdByName}</InfoSectionItem>
        <InfoSectionItem label="Date">
          {formatDate(createTime)}{' '}
          <span className={styles.infoTimeAgo}>({open})</span>
        </InfoSectionItem>
        {bundleInfo !== null && (
          <InfoSectionItem label="Bundle">{bundleInfo}</InfoSectionItem>
        )}
        <InfoSectionItem label={isRejected ? 'Rejected total' : 'New total'}>
          {formatMoney(priceBreakDown.previousTotalPrice ?? 0)}{' '}
          <span
            className={
              priceBreakDown.totalPriceDiff >= 0
                ? styles.totalPlus
                : styles.totalMinus
            }
          >
            {formatSum(priceBreakDown.totalPriceDiff)}
          </span>{' '}
          = {formatMoney(priceBreakDown.newTotalPrice ?? 0)}
        </InfoSectionItem>
        <InfoSectionItem
          label={isRejected ? 'Rejected pro cut' : 'New pro cut'}
        >
          {formatMoney(priceBreakDown.previousTotalProPrice ?? 0)}{' '}
          <span
            className={
              priceBreakDown.totalProPriceDiff >= 0
                ? styles.totalPlus
                : styles.totalMinus
            }
          >
            {formatSum(priceBreakDown.totalProPriceDiff)}
          </span>{' '}
          = {formatMoney(priceBreakDown.newTotalProPrice ?? 0)}
        </InfoSectionItem>
        {percentageNote !== null && (
          <InfoSectionItem label="Notes">{percentageNote}</InfoSectionItem>
        )}
        <InfoSectionItem label="Status">
          <span
            data-test={`scope-change-request-status-${statusText}`}
            className={statusClassName(statusTone)}
          >
            {sidePanelStatusText}
          </span>
        </InfoSectionItem>
      </InfoSectionItems>
    </div>
  )
}

function statusClassName(statusTone: ScopeChangeRequestStatusTone) {
  return match(statusTone, {
    Accepted: styles.statusAccepted,
    Rejected: styles.statusRejected,
    Pending: styles.statusPending,
    Neutral: undefined,
  })
}
